import React, { useState } from "react";
import { Calendar, Clock, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/shared/utils";

interface CalendarOutletProps {
  className?: string;
}

export const CalendarOutlet: React.FC<CalendarOutletProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate] = useState(new Date());

  // Mock data - später durch echte Calendar API ersetzen
  const upcomingEvents = [
    {
      id: "1",
      title: "Team Meeting",
      time: "14:00",
      duration: "1h",
      type: "meeting",
    },
    {
      id: "2",
      title: "Doctor Appointment",
      time: "16:30",
      duration: "30m",
      type: "appointment",
    },
    {
      id: "3",
      title: "Gym Session",
      time: "18:00",
      duration: "1h",
      type: "personal",
    },
  ];

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("de-DE", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case "meeting":
        return "👥";
      case "appointment":
        return "🏥";
      case "personal":
        return "💪";
      default:
        return "📅";
    }
  };

  return (
    <div className={cn("relative", className)}>
      {/* Calendar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-800/80 transition-all duration-200"
        title="Calendar"
      >
        <Calendar className="w-5 h-5 text-blue-500" />
        <div className="hidden md:block text-left">
          <p className="text-xs font-medium text-white">{formatDate(currentDate)}</p>
          <p className="text-xs text-neutral-400">{upcomingEvents.length} events</p>
        </div>
      </button>

      {/* Calendar Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-16 right-4 w-80 bg-neutral-800 rounded-xl border border-neutral-700/50 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-700/50">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-white">Calendar</h3>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-1 text-neutral-400 hover:text-white transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Current Date */}
            <div className="p-4 border-b border-neutral-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-white">
                    {currentDate.toLocaleDateString("de-DE", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}
                  </p>
                  <p className="text-sm text-neutral-400">
                    {upcomingEvents.length} upcoming events
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-1 text-neutral-400 hover:text-white transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button className="p-1 text-neutral-400 hover:text-white transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="p-4">
              <h4 className="text-sm font-medium text-neutral-300 mb-3">Today's Events</h4>
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-3 p-3 bg-neutral-800/50 rounded-lg hover:bg-neutral-800 transition-colors"
                  >
                    <div className="text-2xl">{getEventIcon(event.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{event.title}</p>
                      <div className="flex items-center gap-2 text-xs text-neutral-400">
                        <Clock className="w-3 h-3" />
                        <span>{event.time}</span>
                        <span>•</span>
                        <span>{event.duration}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Add */}
              <button className="w-full mt-4 p-3 border border-dashed border-neutral-600 rounded-lg text-neutral-400 hover:text-white hover:border-neutral-500 transition-colors">
                <div className="flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Add Event</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
